import React, { useEffect } from 'react';
import { Button, Card, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { faFile, faPen, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { RootState } from '../store/rootReducer';

import { fetchMyApplications } from '../store/myApplicationSlice';
import { AppDispatch } from '../store/store';

const Home = (): React.ReactElement => {
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(['applications', 'common']);
  const { myApplications, myApplicationsLoading, myApplicationsError } = useSelector((state: RootState) => state.myApplication);

  useEffect(() => {
    dispatch(fetchMyApplications());
  }, [dispatch]);

  if (myApplicationsLoading === 'pending' || myApplications === null) {
    return <div>{t('common:loading')}</div>;
  }

  if (myApplicationsError !== null) {
    return <div>{t('common:internalError')}</div>;
  }
  return (
    <>
      <Card className="mb-2">
        <Card.Body>
          <Card.Title>
            {currentUser?.name}! {t('welcome')}{' '}
          </Card.Title>
          <Card.Text> {t('toApply')} </Card.Text>
          <Button variant="primary" href="/umsokn">
            {t('apply')}
          </Button>
        </Card.Body>
      </Card>
      <Card className="mb-2">
        <Card.Body>
          <Card.Title>{t('applications')}</Card.Title>

          <div>
            {' '}
            {myApplications.length === 0 ? (
              <div>{t('common:noDataFound')}</div>
            ) : (
              <Table striped bordered hover className="mt-5">
                <thead>
                  <tr>
                    <th>{t('industry')}</th>
                    <th>{t('school')}</th>
                    <th>{t('status')}</th>
                    <th>{t('interview')}</th>

                    {/* <th>{t('createdAt')}</th> */}
                  </tr>
                </thead>
                <tbody>
                  {myApplications.map((myApplication) => (
                    <tr key={myApplication.id}>
                      <td>{myApplication.industry.name}</td>
                      <td>{myApplication.school.name}</td>
                      {myApplication.status === 'NEW' && <td>{t('statusNew')}</td>}
                      {myApplication.status === 'DRAFT' && <td>{t('statusDraft')}</td>}
                      {myApplication.status === 'ON_HOLD' && <td>{t('statusOnHold')}</td>}
                      {myApplication.status === 'RETURNED' && <td>{t('statusReturned')}</td>}
                      {myApplication.status === 'RESUBMITTED' && <td>{t('statusResubmitted')}</td>}
                      {myApplication.status === 'REJECTED' && <td>{t('statusRejected')}</td>}
                      {myApplication.status === 'ACCEPTED' && <td>{t('statusAccepted')}</td>}
                      {myApplication.status === 'CANCELLED' && <td>{t('statusCancelled')}</td>}
                      {(myApplication.applicationQuestionnaireStatus === 'ANSWERED' && <td>{t('statusQuestionnaireAnswered')}</td>) ||
                        (myApplication.applicationQuestionnaireStatus === 'RETURNED' && <td>{t('statusQuestionnaireReturned')}</td>) ||
                        (myApplication.applicationQuestionnaireStatus === 'RESUBMITTED' && <td>{t('statusQuestionnaireResubmitted')}</td>) ||
                        (myApplication.applicationQuestionnaireStatus === 'ASSESSED' && <td>{t('statusQuestionnaireAssessed')}</td>) ||
                        (myApplication.applicationQuestionnaireStatus === 'PUBLISHED' && <td>{t('statusQuestionnairePublished')}</td>) ||
                        (myApplication.status === 'QUESTIONNAIRE_SENT' && <td>{t('statusQuestionnaireSent')}</td>)}
                      {/* <td>{dayjs(myApplication.createdAt).format('DD.MM.YYYY HH:mm')}</td> */}
                      <td>
                        {myApplication.interviewLocation && myApplication.interviewDateTime && (
                          <span className="inline-block">
                            {myApplication.interviewLocation}, {dayjs(myApplication.interviewDateTime).format('DD.MM.YYYY HH:mm')}
                          </span>
                        )}
                        {myApplication.applicationQuestionnaireInterviewLocation && myApplication.applicationQuestionnaireInterviewDateTime && (
                          <span className="inline-block">
                            {myApplication.applicationQuestionnaireInterviewLocation}, {dayjs(myApplication.applicationQuestionnaireInterviewDateTime).format('DD.MM.YYYY HH:mm')}
                          </span>
                        )}
                      </td>

                      <td style={{ width: '190px', textAlign: 'center' }}>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="tooltip-other">
                              {myApplication.status === 'DRAFT' || myApplication.status === 'RETURNED' ? <>{t('noApplicationToView')}</> : <>{t('viewApplication')}</>}
                            </Tooltip>
                          }
                        >
                          <span>
                            <Button
                              type="button"
                              variant="link"
                              href={`/skoda-umsokn/${myApplication.id}`}
                              className="btn-edit"
                              disabled={myApplication.status === 'DRAFT' || myApplication.status === 'RETURNED'}
                            >
                              <FontAwesomeIcon icon={faSearch} />
                            </Button>
                          </span>
                        </OverlayTrigger>

                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id="tooltip-other">
                              {myApplication.status === 'DRAFT' || myApplication.status === 'RETURNED' ? <>{t('editApplication')}</> : <>{t('applicationUneditable')}</>}
                            </Tooltip>
                          }
                        >
                          <span>
                            <Button
                              type="button"
                              variant="link"
                              href={`/skoda-umsokn/${myApplication.id}`}
                              className="btn-edit"
                              disabled={
                                myApplication.status === 'NEW' ||
                                myApplication.status === 'ON_HOLD' ||
                                myApplication.status === 'REJECTED' ||
                                myApplication.status === 'ACCEPTED' ||
                                myApplication.status === 'CANCELLED' ||
                                myApplication.status === 'QUESTIONNAIRE_SENT' ||
                                myApplication.status === 'RESUBMITTED'
                              }
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </Button>
                          </span>
                        </OverlayTrigger>

                        <OverlayTrigger
                          placement="bottom"
                          overlay={<Tooltip id="tooltip-other">{!myApplication.applicationQuestionnaireId ? <>{t('noQuestionnaire')}</> : <>{t('showQuestionnaire')}</>}</Tooltip>}
                        >
                          <span>
                            <Button
                              type="button"
                              variant="link"
                              href={`/matslisti/${myApplication.applicationQuestionnaireId}`}
                              className="btn-edit"
                              disabled={!myApplication.applicationQuestionnaireId}
                            >
                              <FontAwesomeIcon icon={faFile} />
                            </Button>
                          </span>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default Home;
